/* -disable react/no-unused-state */
/*eslint-disable react/no-deprecated */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { groupBy } from 'lodash';

// Third Party Components
import { GoogleMap, Marker, OverlayView, withScriptjs, withGoogleMap } from 'react-google-maps';

import MarkerClusterer from 'react-google-maps/lib/components/addons/MarkerClusterer';

// Components
import GeneralMarker from './GeneralMarker';
import MapTimer from './MapTimer';
import DeliveryModal from './DeliveryModal';
import LoadingIndicator from '../../../../../../components/LoadingIndicator';

// util
import { performanceStatus } from '../../../../../Tracking/utils/delivery-status';
import { mapCenterCalculate } from '../../../../../../utils/map-center-calculate';

// thunks
import {
  getNfListLocation,
  MAP_EXHIBITION_TYPE,
} from '../../../../../../redux/dashboard-real-time';
import { COORD_MATCH_TOLERANCE, MAX_ZOOM } from '../../../../constants';

// selectors
import {
  getDashboardRealTimeUI,
  getDeliveryStatusDeliveriesByAccType,
} from '../../../../../../redux/selectors';

// css
import './styles.css';
import {
  getDeliveryStatusDeliveriesList,
  storeKeys,
} from '../../../../../../redux/delivery-status';
import { offsetCenter } from '../../../../../../utils/offsetCoodinates';
import { LocationTypeFilter } from './LocationType';
class DashboardRealTimeMap extends Component {
  constructor(props) {
    super(props);

    this.state = {
      truckPositions: [],
      activeClusterData: null,
      mapPinPosition: null,
    };

    this.googleMap = React.createRef();

    this._lastActiveCluster = null;

    this._handleClusterClick = this._handleClusterClick.bind(this);
    this._handleClusteringEnd = this._handleClusteringEnd.bind(this);
    this._handleMapClick = this._handleMapClick.bind(this);
    this._handleZoomChanged = this._handleZoomChanged.bind(this);
    this._handleMarkerClick = this._handleMarkerClick.bind(this);
    this._handlePinClick = this._handlePinClick.bind(this);
  }

  _handleMapClick(e) {
    const latLng = e?.latLng;
    if (!latLng) return;

    this.setState({
      mapPinPosition: { lat: latLng.lat(), lng: latLng.lng() },
    });
  }

  _allMarkersAtSamePosition(markers) {
    if (!markers || markers.length <= 1) return true;
    const first = markers[0].getPosition();
    return markers.every(m => {
      const pos = m.getPosition();
      return (
        Math.abs(pos.lat() - first.lat()) < COORD_MATCH_TOLERANCE &&
        Math.abs(pos.lng() - first.lng()) < COORD_MATCH_TOLERANCE
      );
    });
  }

  _findLocationByPosition(pos) {
    return this._getNfListByActivePerformanceStatusCode().find(
      loc =>
        Math.abs(Number(loc.latitude) - pos.lat()) < COORD_MATCH_TOLERANCE &&
        Math.abs(Number(loc.longitude) - pos.lng()) < COORD_MATCH_TOLERANCE,
    );
  }

  _handleClusterClick(cluster) {
    const markers = cluster.getMarkers();
    if (!markers || markers.length === 0) return;

    const currentZoom = this.googleMap.current.getZoom();
    const isLastLevel = this._allMarkersAtSamePosition(markers);

    if (!isLastLevel) {
      this._lastActiveCluster = null;
      this.setState({ activeClusterData: null });
      this.googleMap.current.fitBounds(cluster.getBounds());
      return;
    }

    const center = cluster.getCenter();
    const clusterData = {
      center: { lat: center.lat(), lng: center.lng() },
      count: cluster.getSize(),
    };

    const firstPos = markers[0].getPosition();
    const matchingLocation = this._findLocationByPosition(firstPos);

    this._lastActiveCluster = clusterData;
    this.setState({ activeClusterData: clusterData });

    if (!matchingLocation) return;

    const targetZoom = Math.max(currentZoom, MAX_ZOOM);
    const newCenter = offsetCenter(
      firstPos,
      mapCenterCalculate(848),
      0,
      this.googleMap.current,
      targetZoom,
    );
    this.props.renderMarkerTooltip({ latLng: firstPos }, matchingLocation, newCenter, targetZoom);
  }

  _getMapPinIcon() {
    const google = window.google;
    const size = google && google.maps ? new google.maps.Size(36, 36) : undefined;
    return {
      url: '/map-marker.png',
      ...(size ? { scaledSize: size } : {}),
    };
  }

  componentDidMount() {
    const { setMapRef } = this.props;
    this._requestMapData();
    setMapRef(this.googleMap.current);
  }

  componentWillReceiveProps(newProps) {
    const { hideMarkerTooltip } = this.props;

    // If component has a new active tab or nfList is updated,
    // fetch location using those props
    if (
      this.props.deliveryStatusActiveTab !== newProps.deliveryStatusActiveTab
    ) {
      this.props.dispatch(
        getNfListLocation(newProps.deliveryStatusActiveTab, newProps.nfList),
      );

      hideMarkerTooltip();
    }
  }

  async _requestMapData() {
    const deliveryData = await this.props.dispatch(
      getDeliveryStatusDeliveriesList(
        false,
        storeKeys.REAL_TIME,
        !!this.props.notPassCompanyId,
      ),
    );
    
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const nfListToUse = this.props.nfList?.length > 0 
      ? this.props.nfList 
      : (deliveryData || []);
    
    if (nfListToUse.length > 0) {
      const hasId = this.props.nfIdFromUrl;
      const callback = hasId 
        ? () => {
            setTimeout(() => {
              if (this.googleMap.current) {
                this.props.getPositionFromTracking(this.googleMap.current);
              }
            }, 150);
          }
        : undefined;
      
      await this.props.dispatch(
        getNfListLocation(
          this.props.deliveryStatusActiveTab,
          nfListToUse,
          callback,
        ),
      );
    }
  }

  _setTruckPositions() {
    this.setState({
      truckPositions: this._getNfListByActivePerformanceStatusCode(),
    });
  }

  _getNfListByActivePerformanceStatusCode() {
    let newLocationList = [];

    const {
      deliveryStatusActiveTab,
      lateLocationData,
      lateRiskLocationData,
      onTimeLocationData,
    } = this.props;

    const statusDataMap = {
      [performanceStatus.LATE]: lateLocationData,
      [performanceStatus.LATE_RISK]: lateRiskLocationData,
      [performanceStatus.ONTIME]: onTimeLocationData,
    };

    const locationList = deliveryStatusActiveTab.flatMap(status => {
      const data = statusDataMap[status] || [];
      return data.map(item => ({
        ...item,
        status: item.performance,
      }));
    });

    const nfIdToCpfMap = {};
    this.props.nfList?.forEach(nf => {
      if (nf?.cpfMotorista) {
        nfIdToCpfMap[nf.id] = nf.cpfMotorista.trim();
      }
    });

    locationList?.map(location => {
      if (
        (this.props.mapExhibition === MAP_EXHIBITION_TYPE.APP_LOCATION ||
          this.props.mapExhibition === null ||
          this.props.mapExhibition === undefined) &&
        location?.appLocation !== null
      ) {
        const driverCpf = nfIdToCpfMap?.[location?.nfId] || null;

        newLocationList.push({
          companyId: location.companyId,
          nfId: location.nfId,
          performance: location.performance,
          status: location.status,
          shippingGroupId: location.shippingGroupId,
          latitude: location.appLocation.latitude,
          longitude: location.appLocation.longitude,
          landmark: location.appLocation.landmark,
          time: location.appLocation.time,
          driverCpf: driverCpf,
          isAppLocation: true,
        });
      }
      if (
        (this.props.mapExhibition === MAP_EXHIBITION_TYPE.TRACKER_LOCATION ||
          this.props.mapExhibition === null ||
          this.props.mapExhibition === undefined) &&
        location?.trackerLocation !== null
      ) {
        newLocationList.push({
          companyId: location.companyId,
          nfId: location.nfId,
          performance: location.performance,
          status: location.status,
          shippingGroupId: location.shippingGroupId,
          latitude: location.trackerLocation.latitude,
          longitude: location.trackerLocation.longitude,
          landmark: location.trackerLocation.landmark,
          time: location.trackerLocation.time,
          vehicleTracker: location.trackerLocation.vehicleDetail.tracker,
          vehiclePlate: location.trackerLocation.vehicleDetail.plate
            ? location?.trackerLocation?.vehicleDetail?.plate?.trim()
            : null,
          vehicleIgnition: location.trackerLocation.vehicleDetail.ignition,
          driverCpf: nfIdToCpfMap?.[location?.nfId] || null,
          isAppLocation: false,
        });
      }
    });

    const nfIdToNfMap = {};
    this.props.nfList?.forEach(nf => {
      nfIdToNfMap[nf?.id] = nf;
    });

    const { locationsWithCpf, locationsWithoutCpf } = newLocationList.reduce(
      (acc, loc) => {
        if (!loc) return acc;

        if (loc.driverCpf) {
          acc.locationsWithCpf.push(loc);
          return acc;
        }

        acc.locationsWithoutCpf.push(loc);
        return acc;
      },
      { locationsWithCpf: [], locationsWithoutCpf: [] }
    );

    const locationByCpf = groupBy(locationsWithCpf, 'driverCpf');

    const nfListGroup = {};

    this.props.nfList?.forEach(nf => {
      if (nf?.cpfMotorista) {
        const normalizedCpf = nf?.cpfMotorista?.trim();
        if (!nfListGroup[normalizedCpf]) {
          nfListGroup[normalizedCpf] = [];
        }
        nfListGroup?.[normalizedCpf]?.push(nf);
      }
    });

    const appLocationByCpf = [];

    Object.keys(locationByCpf)?.forEach(key => {
      const normalizedKey = key ? key.trim() : null;

      if (normalizedKey && nfListGroup[normalizedKey]) {
        locationByCpf[key]?.forEach(location => {
          const firstNf = nfListGroup[normalizedKey][0];
          const locationData = {
            ...location,
            nfList: nfListGroup[normalizedKey],
            isKeyAccount: firstNf ? firstNf?.tms?.deliveryImportant : false,
            performanceStatusCode: firstNf
              ? firstNf?.tms?.performanceStatus
              : 2,
          };
          delete locationData.nfId;
          appLocationByCpf.push(locationData);
        });
      }
    });

    locationsWithoutCpf?.forEach(location => {
      const nf = nfIdToNfMap[location?.nfId];
      if (nf) {
        const locationData = {
          ...location,
          nfList: [nf],
          isKeyAccount: nf?.tms ? nf?.tms?.deliveryImportant : false,
          performanceStatusCode: nf?.tms ? nf?.tms?.performanceStatus : 2,
        };
        delete locationData.nfId;
        appLocationByCpf.push(locationData);
      }
    });

    return appLocationByCpf;
  }

  _onUpdateTimer() {
    this.props.dispatch(
      getNfListLocation(this.props.deliveryStatusActiveTab, this.props.nfList),
    );
  }

  _onRetry() {
    this.props.dispatch(
      getNfListLocation(this.props.deliveryStatusActiveTab, this.props.nfList),
    );
  }

  _handleZoomChanged() {
    this.props.onZoomChanged();
  }

  _handleClusteringEnd(clusterer) {
    if (!this._lastActiveCluster) return;
    const { center, count } = this._lastActiveCluster;

    const survived = clusterer.getClusters().some(c => {
      const pos = c.getCenter();
      return (
        Math.abs(pos.lat() - center.lat) < COORD_MATCH_TOLERANCE &&
        Math.abs(pos.lng() - center.lng) < COORD_MATCH_TOLERANCE &&
        c.getSize() === count
      );
    });

    if (survived !== !!this.state.activeClusterData) {
      this.setState({ activeClusterData: survived ? this._lastActiveCluster : null });
    }
  }

  _handleMarkerClick(e, location) {
    this._lastActiveCluster = null;
    this.setState({ activeClusterData: null });
    const currentZoom = this.googleMap.current.getZoom();
    const tooltipZoom = Math.max(currentZoom, MAX_ZOOM);
    const newCenter = offsetCenter(
      e.latLng,
      mapCenterCalculate(848),
      0,
      this.googleMap.current,
      tooltipZoom,
    );
    this.props.renderMarkerTooltip(e, location, newCenter, tooltipZoom);
  }

  _handlePinClick() {
    this.setState({ mapPinPosition: null });
  }

  _getOverlayOffset(width, height) {
    return { x: -(width / 2), y: -(height / 2) };
  }

  render() {
    const {
      ui,
      activeNf,
      hideMarkerTooltip,
      zoom,
      nfList,
      mapCenter,
    } = this.props;

    const { mapPinPosition } = this.state;

    return (
      <div>
        <GoogleMap
          ref={this.googleMap}
          defaultZoom={zoom}
          zoom={zoom}
          defaultCenter={mapCenter}
          center={mapCenter}
          onClick={this._handleMapClick}
          onZoomChanged={this._handleZoomChanged}
          defaultOptions={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            gestureHandling: 'cooperative',
          }}
        >
          {mapPinPosition && (
            <Marker
              position={mapPinPosition}
              icon={this._getMapPinIcon()}
              onClick={this._handlePinClick}
            />
          )}

          <MarkerClusterer
            clusterClass="DashboardRealTimeMap__cluster"
            averageCenter
            enableRetinaIcons
            gridSize={30}
            minimumClusterSize={3}
            zoomOnClick={false}
            onClick={this._handleClusterClick}
            onClusteringEnd={this._handleClusteringEnd}
          >
            {this._getNfListByActivePerformanceStatusCode().map(
              (location, index) => {
                return (
                  <GeneralMarker
                    key={Number(index)}
                    position={{
                      lat: Number(location.latitude),
                      lng: Number(location.longitude),
                    }}
                    status={location.status}
                    keyAccount={
                      location.isKeyAccount ? location.isKeyAccount : false
                    } // TO DO remover ternarios depois de ajuste na API
                    shippingGroupId={
                      location.shippingGroupId ? location.shippingGroupId : 1
                    }
                    isActive={
                      !!(location.nfList &&
                        location.nfList.find(el => el.id === activeNf.nfId))
                    }
                    isAppLocation={location.isAppLocation}
                    onClick={e => this._handleMarkerClick(e, location)}
                  />
                );
              },
            )}
          </MarkerClusterer>

          {this.state.activeClusterData && (
            <OverlayView
              position={this.state.activeClusterData.center}
              mapPaneName={OverlayView.FLOAT_PANE}
              getPixelPositionOffset={this._getOverlayOffset}
            >
              <div className="DashboardRealTimeMap__active-cluster">
                {this.state.activeClusterData.count}
              </div>
            </OverlayView>
          )}

          {activeNf && activeNf.isVisible && (
            <DeliveryModal
              nfData={activeNf.data}
              initialSlide={activeNf.expanded}
              onClose={hideMarkerTooltip}
            />
          )}
        </GoogleMap>

        {ui.isFetching && (
          <div className="DashboardRealTimeMap__loading-indicator">
            <LoadingIndicator theme="dark" />
          </div>
        )}

        {!ui.isFetching && nfList.length > 0 && (
          <div className="DashboardRealTimeMap__map-timer">
            <MapTimer
              onUpdate={this._onUpdateTimer}
              totalTime={1000 * 60 * 5}
            />
          </div>
        )}

        {!ui.isFetching && nfList.length > 0 && (
          <div className="DashboardRealTimeMap__location-type-filter">
            <LocationTypeFilter
              isCollapsed={true}
              onChange={this.props.changeMapExhibition}
              selectedType={this.props.mapExhibition}
            />
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const isKeyAcc = null;

  const onTime = [];
  const lateRisk = [];
  const late = [];

  state.dashboardRealTime.onTime.data.map(item => {
    if (item.performance === performanceStatus.ONTIME) {
      onTime.push(item);
    }
    if (item.performance === performanceStatus.LATE_RISK) {
      lateRisk.push(item);
    }
    if (item.performance === performanceStatus.LATE) {
      late.push(item);
    }
  });

  return {
    mapExhibition: ownProps.mapExhibition,
    lateLocationData: late,
    lateRiskLocationData: lateRisk,
    onTimeLocationData: onTime,
    ui: getDashboardRealTimeUI(state),
    nfList: getDeliveryStatusDeliveriesByAccType(
      isKeyAcc,
      ownProps.deliveryStatusActiveTab,
      storeKeys.REAL_TIME,
    )(state),
  };
}

export default connect(mapStateToProps)(
  withScriptjs(withGoogleMap(DashboardRealTimeMap)),
);
